/* Mattress Town theme scripts. No dependencies. */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  /* Wording comes from the theme content (Edit default theme content), via layout/theme.liquid. */
  const decode = (value) => {
    const area = document.createElement('textarea');
    area.innerHTML = value;
    return area.value;
  };
  const text = (key, fallback) => (theme.strings && theme.strings[key] ? decode(theme.strings[key]) : fallback);
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const desktop = () => window.matchMedia('(min-width: 990px)').matches;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    const fmt = String(format || theme.moneyFormat || '£{{amount}}').replace(/<[^>]*>/g, '');
    const value = Number(cents) || 0;
    const number = (decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return number(0);
        case 'amount_with_comma_separator': return number(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return number(0, '.', ',');
        case 'amount_with_space_separator': return number(2, ' ', ',');
        case 'amount_with_apostrophe_separator': return number(2, "'", '.');
        default: return number(2);
      }
    });
  }

  /* ---------- Dialogs (drawers, modals, search) ---------- */
  function openDialog(id, opener) {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return false;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('no-scroll');
    const focusTarget = $('[data-predictive-input]', dialog) || $('[autofocus]', dialog);
    if (focusTarget) window.setTimeout(() => focusTarget.focus(), 40);
    return true;
  }

  document.addEventListener('close', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!$('dialog[open]')) document.documentElement.classList.remove('no-scroll');
    if (dialog._opener && document.contains(dialog._opener)) dialog._opener.focus();
  }, true);

  document.addEventListener('click', (event) => {
    const target = event.target;

    // Clicking the backdrop closes a dialog
    if (target instanceof HTMLDialogElement && target.open) {
      target.close();
      return;
    }

    const opener = target.closest('[data-open-dialog]');
    if (opener) {
      event.preventDefault();
      openDialog(opener.dataset.openDialog, opener);
      return;
    }

    const closer = target.closest('[data-close-dialog]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) dialog.close();
      return;
    }

    const cartToggle = target.closest('[data-cart-toggle]');
    if (cartToggle && theme.cartType === 'drawer' && window.location.pathname !== routes.cart) {
      if (openDialog('cart-drawer', cartToggle)) event.preventDefault();
    }
  });

  /* ---------- Announcement bar (rotates on small screens) ---------- */
  function initAnnouncements(root = document) {
    $$('[data-announcement]', root).forEach((bar) => {
      const messages = $$('.announcement__message', bar);
      if (messages.length < 2 || bar._timer || reducedMotion()) return;
      let index = 0;
      let paused = false;
      bar.addEventListener('mouseenter', () => { paused = true; });
      bar.addEventListener('mouseleave', () => { paused = false; });
      bar.addEventListener('focusin', () => { paused = true; });
      bar.addEventListener('focusout', () => { paused = false; });
      bar._timer = window.setInterval(() => {
        if (paused || desktop()) return;
        messages[index].classList.remove('is-active');
        index = (index + 1) % messages.length;
        messages[index].classList.add('is-active');
      }, 5000);
    });
  }

  /* ---------- Header dropdowns ---------- */
  function closeDropdowns(except) {
    $$('[data-dropdown][open]').forEach((dropdown) => {
      if (dropdown !== except) dropdown.open = false;
    });
  }

  function initDropdowns(root = document) {
    $$('[data-dropdown]', root).forEach((dropdown) => {
      if (dropdown._bound) return;
      dropdown._bound = true;
      const item = dropdown.closest('.site-nav__item') || dropdown;
      let timer;
      const canHover = () => window.matchMedia('(hover: hover) and (min-width: 1100px)').matches;
      item.addEventListener('mouseenter', () => {
        if (!canHover()) return;
        window.clearTimeout(timer);
        closeDropdowns(dropdown);
        dropdown.open = true;
      });
      item.addEventListener('mouseleave', () => {
        if (!canHover()) return;
        timer = window.setTimeout(() => { dropdown.open = false; }, 160);
      });
      dropdown.addEventListener('toggle', () => {
        if (dropdown.open) closeDropdowns(dropdown);
      });
    });
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-dropdown]')) closeDropdowns();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = $('[data-dropdown][open]');
    if (open) {
      open.open = false;
      const summary = $('summary', open);
      if (summary) summary.focus();
    }
    const facets = $('[data-facets].is-open');
    if (facets) closeFacets(facets);
  });

  /* ---------- Search suggestions ---------- */
  let searchTimer;
  let searchController;
  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-predictive-input]');
    if (!input || !theme.predictiveSearch) return;
    const container = input.closest('dialog') || document;
    const results = $('[data-predictive-results]', container);
    if (!results) return;
    window.clearTimeout(searchTimer);
    const query = input.value.trim();
    if (!query) {
      results.innerHTML = '';
      return;
    }
    searchTimer = window.setTimeout(async () => {
      if (searchController) searchController.abort();
      searchController = new AbortController();
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,article,page,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
        section_id: 'predictive-search'
      });
      try {
        const response = await fetch(`${routes.predictiveSearch}?${params}`, { signal: searchController.signal });
        if (!response.ok) throw new Error(String(response.status));
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = $('[data-predictive-content]', doc);
        results.innerHTML = content ? content.outerHTML : '';
      } catch (error) {
        if (error.name !== 'AbortError') results.innerHTML = '';
      }
    }, 220);
  });

  /* ---------- Quantity buttons ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-qty-change]');
    if (!button) return;
    const wrapper = button.closest('[data-qty]');
    const input = wrapper && $('input', wrapper);
    if (!input) return;
    const min = input.min !== '' ? Number(input.min) : 0;
    const next = Math.max(min, (Number(input.value) || 0) + Number(button.dataset.qtyChange));
    if (next === Number(input.value)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Basket ---------- */
  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = Number(count) === 0;
    });
  }

  function renderCartDrawer(sections) {
    const html = sections && sections['cart-drawer'];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = $('[data-cart-drawer-content]', doc);
    const current = $('[data-cart-drawer-content]');
    if (fresh && current) current.replaceWith(fresh);
    if (fresh) updateCartCount(fresh.dataset.itemCount);
  }

  async function postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    return data;
  }

  async function addToCart(form) {
    const body = new FormData(form);
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);
    const response = await fetch(`${routes.cartAdd}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || text('addError', 'That couldn\'t be added to your basket.'));
    return data;
  }

  async function changeLine(line, quantity) {
    const drawer = $('[data-cart-drawer-content]');
    const row = drawer && $(`[data-line="${line}"]`, drawer);
    if (row) row.classList.add('is-updating');
    try {
      const data = await postJSON(`${routes.cartChange}.js`, {
        line: Number(line),
        quantity: Number(quantity),
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      });
      renderCartDrawer(data.sections);
    } catch (error) {
      if (row) row.classList.remove('is-updating');
      showDrawerError(error.message);
    }
  }

  function showDrawerError(message) {
    const drawer = $('[data-cart-drawer-content]');
    if (!drawer) return;
    let note = $('.cart-drawer__error', drawer);
    if (!note) {
      note = document.createElement('p');
      note.className = 'form-message form-message--error cart-drawer__error';
      note.setAttribute('role', 'alert');
      const header = $('.drawer__header', drawer);
      if (header) header.after(note);
    }
    note.textContent = message;
  }

  document.addEventListener('change', (event) => {
    const lineInput = event.target.closest('[data-line-quantity]');
    if (lineInput) {
      changeLine(lineInput.dataset.lineQuantity, Math.max(0, Number(lineInput.value) || 0));
      return;
    }
    const pageInput = event.target.closest('[data-cart-page-qty]');
    if (pageInput) {
      const form = pageInput.closest('form');
      window.clearTimeout(form._timer);
      form._timer = window.setTimeout(() => form.submit(), 450);
    }
  });

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-line-remove]');
    if (remove) changeLine(remove.dataset.lineRemove, 0);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || theme.cartType !== 'drawer' || !window.fetch) return;
    event.preventDefault();
    const button = $('[data-add-button]', form);
    const error = $('[data-form-error]', form);
    if (!button || button.disabled || button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    if (error) error.hidden = true;
    try {
      const data = await addToCart(form);
      renderCartDrawer(data.sections);
      openDialog('cart-drawer', button);
    } catch (err) {
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      }
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
    }
  });

  /* ---------- Product page ---------- */
  function goToMedia(section, mediaId) {
    const slides = $('[data-gallery-slides]', section);
    const slide = slides && $(`[data-media-id="${mediaId}"]`, slides);
    if (!slide) return;
    const left = slide.getBoundingClientRect().left - slides.getBoundingClientRect().left + slides.scrollLeft;
    slides.scrollTo({ left, behavior: reducedMotion() ? 'auto' : 'smooth' });
    $$('[data-media-target]', section).forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.dataset.mediaTarget === String(mediaId));
    });
  }

  function initGallery(section) {
    const slides = $('[data-gallery-slides]', section);
    if (!slides) return;
    const thumbs = $$('[data-media-target]', section);
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => goToMedia(section, thumb.dataset.mediaTarget));
    });
    if (!('IntersectionObserver' in window) || thumbs.length === 0) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
        const id = entry.target.dataset.mediaId;
        thumbs.forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.mediaTarget === id));
      });
    }, { root: slides, threshold: [0.6] });
    $$('[data-media-id]', slides).forEach((slide) => observer.observe(slide));
  }

  function initProducts(root = document) {
    $$('[data-product]', root).forEach((section) => {
      if (section._init) return;
      section._init = true;
      const jsonEl = $('[data-product-json]', section);
      if (!jsonEl) return;
      let product;
      try {
        product = JSON.parse(jsonEl.textContent);
      } catch (error) {
        return;
      }
      const picker = $('[data-variant-picker]', section);
      const form = $('[data-product-form]', section);
      const idInput = form && $('[data-variant-id]', form);
      const fieldsets = picker ? $$('fieldset[data-option-index]', picker) : [];
      const price = $('[data-price]', section);
      const addButton = $('[data-add-button]', section);
      const addLabel = $('[data-add-label]', section);
      const sticky = $('[data-sticky-atc]', section);
      const stickyButton = sticky && $('[data-sticky-add]', sticky);

      const selectedOptions = () => fieldsets.map((fieldset) => {
        const checked = $('input:checked', fieldset);
        return checked ? checked.value : null;
      });
      const findVariant = (options) => product.variants.find((variant) => variant.options.every((value, i) => value === options[i]));

      function markAvailability(options) {
        fieldsets.forEach((fieldset, index) => {
          $$('input', fieldset).forEach((input) => {
            const test = options.slice();
            test[index] = input.value;
            const match = findVariant(test);
            const label = $(`label[for="${input.id}"]`, fieldset);
            if (label) label.classList.toggle('is-unavailable', !match || !match.available);
          });
        });
      }

      function setButton(enabled, text) {
        if (addButton) addButton.disabled = !enabled;
        if (addLabel) addLabel.textContent = text;
        if (stickyButton) stickyButton.disabled = !enabled;
      }

      function renderPrice(variant) {
        if (!price) return;
        const current = $('[data-price-current]', price);
        const compare = $('[data-price-compare]', price);
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (current) current.textContent = formatMoney(variant.price);
        price.classList.toggle('price--sale', Boolean(onSale));
        if (compare) {
          compare.hidden = !onSale;
          compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        }
      }

      function update() {
        const options = selectedOptions();
        fieldsets.forEach((fieldset, i) => {
          const label = $('[data-option-selected]', fieldset);
          if (label) label.textContent = options[i] || '';
        });
        markAvailability(options);
        const variant = findVariant(options);
        if (!variant) {
          setButton(false, text('unavailable', 'Unavailable'));
          return;
        }
        if (idInput) idInput.value = variant.id;
        setButton(variant.available, variant.available ? text('addToBasket', 'Add to basket') : text('soldOut', 'Sold out'));
        renderPrice(variant);
        if (variant.featured_media) goToMedia(section, variant.featured_media.id);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        if (sticky) {
          const stickyVariant = $('[data-sticky-variant]', sticky);
          const stickyPrice = $('[data-sticky-price]', sticky);
          if (stickyVariant) stickyVariant.textContent = variant.title;
          if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
        }
      }

      if (picker) {
        picker.addEventListener('change', update);
        markAvailability(selectedOptions());
      }

      if (sticky && addButton && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sticky.classList.toggle('is-visible', show);
          sticky.setAttribute('aria-hidden', String(!show));
          if (stickyButton) stickyButton.tabIndex = show ? 0 : -1;
        });
        observer.observe(addButton);
        if (stickyButton) {
          stickyButton.addEventListener('click', () => {
            if (typeof form.requestSubmit === 'function') form.requestSubmit(addButton);
            else addButton.click();
          });
        }
      }

      initGallery(section);
    });
  }

  /* ---------- Product suggestions ---------- */
  function loadRecommendations(element) {
    if (element._loaded) return;
    element._loaded = true;
    const url = element.dataset.url;
    if (!url) return;
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) {
          element.innerHTML = fresh.innerHTML;
        } else {
          element.hidden = true;
        }
      })
      .catch(() => { element.hidden = true; });
  }

  function initRecommendations(root = document) {
    $$('[data-recommendations]', root).forEach((element) => {
      if ($('.container', element)) return;
      if (!('IntersectionObserver' in window)) {
        loadRecommendations(element);
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          loadRecommendations(element);
        }
      }, { rootMargin: '0px 0px 600px 0px' });
      observer.observe(element);
    });
  }

  /* ---------- Collection filters and sorting ---------- */
  function submitFacets(form) {
    const params = new URLSearchParams(new FormData(form));
    Array.from(params.entries()).forEach(([key, value]) => {
      if (value === '') params.delete(key);
    });
    params.delete('page');
    const action = form.getAttribute('action') || window.location.pathname;
    window.location.href = `${action.split('?')[0]}?${params.toString()}#results`;
  }

  function closeFacets(facets) {
    facets.classList.remove('is-open');
    document.documentElement.classList.remove('no-scroll');
    const opener = $(`[aria-controls="${facets.id}"]`);
    if (opener) {
      opener.setAttribute('aria-expanded', 'false');
      opener.focus();
    }
  }

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-facets-open]');
    if (open) {
      const facets = document.getElementById(open.getAttribute('aria-controls'));
      if (!facets) return;
      facets.classList.add('is-open');
      open.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('no-scroll');
      const close = $('[data-facets-close]', facets);
      if (close) close.focus();
      return;
    }
    const close = event.target.closest('[data-facets-close]');
    if (close) closeFacets(close.closest('[data-facets]'));
  });

  document.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-sort-select]');
    if (sort) {
      if (sort.form) {
        submitFacets(sort.form);
      } else {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      }
      return;
    }
    const form = event.target.closest('[data-facets-form]');
    if (form && desktop()) submitFacets(form);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-facets-form]');
    if (!form) return;
    event.preventDefault();
    submitFacets(form);
  });

  /* ---------- Start up, and re-run when sections change in the theme editor ---------- */
  function init(root = document) {
    initAnnouncements(root);
    initDropdowns(root);
    initProducts(root);
    initRecommendations(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const house = event.target.closest('.street__plot');
    if (house) house.scrollIntoView({ block: 'nearest', inline: 'center' });
  });

  window.MattressTown = { formatMoney, openDialog };
})();

/* Header: if the menu doesn't fit beside the icons, show the menu button instead (see .site-header--compact in base.css). */
(() => {
  const fit = () => {
    document.querySelectorAll('[data-header]').forEach((header) => {
      const nav = header.querySelector('.site-nav');
      if (!nav) return;
      header.classList.remove('site-header--compact');
      if (!window.matchMedia('(min-width: 1100px)').matches) return;
      if (nav.scrollWidth > nav.clientWidth + 1) header.classList.add('site-header--compact');
    });
  };
  let frame;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(fit);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fit);
  else fit();
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
  document.addEventListener('shopify:section:load', schedule);
})();

/* Cookie banner and cookie settings (sections/cookie-banner.liquid).
   Choices are saved through Shopify's Customer Privacy API, so Shopify's own tracking and every app pixel
   (Google & YouTube, Facebook & Instagram, TikTok and so on) follow them, and Google receives the matching
   consent mode signals. Nothing here records a choice unless the visitor presses a button. */
(() => {
  const PURPOSES = ['analytics', 'marketing', 'preferences'];
  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const rootEl = () => document.querySelector('[data-cookie-consent]');
  const bannerEl = () => document.querySelector('[data-cookie-banner]');
  const dialogEl = () => document.getElementById('cookie-preferences');
  const choicesFor = (value) => PURPOSES.reduce((all, purpose) => Object.assign(all, { [purpose]: value }), {});
  let apiPromise;
  let toastTimer;
  let badgeRequested = false;

  function loadApi() {
    if (apiPromise) return apiPromise;
    apiPromise = new Promise((resolve) => {
      const shopify = window.Shopify;
      const ready = () => {
        const api = window.Shopify && window.Shopify.customerPrivacy;
        resolve(api && typeof api.setTrackingConsent === 'function' && typeof api.shouldShowBanner === 'function' ? api : null);
      };
      if (shopify && shopify.customerPrivacy && typeof shopify.customerPrivacy.shouldShowBanner === 'function') {
        ready();
        return;
      }
      if (!shopify || typeof shopify.loadFeatures !== 'function') {
        resolve(null);
        return;
      }
      const timer = window.setTimeout(() => resolve(null), 10000);
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          window.clearTimeout(timer);
          if (error) resolve(null);
          else ready();
        });
      } catch (error) {
        window.clearTimeout(timer);
        resolve(null);
      }
    });
    return apiPromise;
  }

  function showBanner() {
    const banner = bannerEl();
    if (!banner) return;
    banner.hidden = false;
    document.documentElement.classList.add('has-cookie-banner');
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => banner.classList.add('is-visible')));
  }

  function hideBanner() {
    const banner = bannerEl();
    if (!banner) return;
    banner.classList.remove('is-visible');
    banner.hidden = true;
    document.documentElement.classList.remove('has-cookie-banner');
  }

  function setError(message) {
    document.querySelectorAll('[data-cookie-error]').forEach((el) => {
      el.textContent = message || '';
      el.hidden = !message;
    });
  }

  function toast(message) {
    const el = document.querySelector('[data-cookie-toast]');
    if (!el || !message) return;
    el.textContent = message;
    window.requestAnimationFrame(() => el.classList.add('is-visible'));
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      el.classList.remove('is-visible');
      window.setTimeout(() => { el.textContent = ''; }, 250);
    }, 3200);
  }

  // Google's store rating badge sets a Google cookie, so it only loads once marketing cookies are allowed.
  function loadGoogleBadge() {
    const root = rootEl();
    if (badgeRequested || !root || !root.dataset.googleMerchant || designMode()) return;
    loadApi().then((api) => {
      if (badgeRequested || !api || !api.marketingAllowed()) return;
      badgeRequested = true;
      const script = document.createElement('script');
      script.src = 'https://www.gstatic.com/shopping/merchant/merchantwidget.js';
      script.async = true;
      script.addEventListener('load', () => {
        if (!window.merchantwidget || typeof window.merchantwidget.start !== 'function') return;
        const id = root.dataset.googleMerchant;
        window.merchantwidget.start({
          merchant_id: /^\d+$/.test(id) ? Number(id) : id,
          position: root.dataset.googlePosition || 'LEFT_BOTTOM',
          sideMargin: 36,
          bottomMargin: 36,
          mobileSideMargin: 16,
          mobileBottomMargin: 46,
        });
      });
      document.head.appendChild(script);
    });
  }

  function save(choices) {
    const root = rootEl();
    setError('');
    return loadApi()
      .then((api) => new Promise((resolve) => {
        if (!api) {
          resolve(false);
          return;
        }
        const timer = window.setTimeout(() => resolve(false), 10000);
        try {
          api.setTrackingConsent(choices, (result) => {
            window.clearTimeout(timer);
            resolve(!(result && result.error));
          });
        } catch (error) {
          window.clearTimeout(timer);
          resolve(false);
        }
      }))
      .then((saved) => {
        if (!saved) {
          setError(root ? root.dataset.error : '');
          return false;
        }
        hideBanner();
        const dialog = dialogEl();
        if (dialog && dialog.open) dialog.close();
        toast(root ? root.dataset.saved : '');
        loadGoogleBadge();
        return true;
      });
  }

  function openSettings(opener) {
    const dialog = dialogEl();
    if (!dialog) {
      // The theme banner is switched off: fall back to Shopify's own banner, if it's on.
      if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') window.privacyBanner.showPreferences();
      return;
    }
    setError('');
    loadApi().then((api) => {
      // Show what is allowed right now. Before anyone has chosen, that's everything off.
      const allowed = {
        analytics: Boolean(api && api.analyticsProcessingAllowed()),
        marketing: Boolean(api && api.marketingAllowed()),
        preferences: Boolean(api && api.preferencesProcessingAllowed()),
      };
      dialog.querySelectorAll('[data-cookie-purpose]').forEach((box) => { box.checked = allowed[box.dataset.cookiePurpose]; });
      if (window.MattressTown && typeof window.MattressTown.openDialog === 'function') window.MattressTown.openDialog('cookie-preferences', opener);
      else if (!dialog.open) dialog.showModal();
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-cookie-accept]')) {
      save(choicesFor(true));
      return;
    }
    if (target.closest('[data-cookie-reject]')) {
      save(choicesFor(false));
      return;
    }
    if (target.closest('[data-cookie-save]')) {
      const choices = choicesFor(false);
      const dialog = dialogEl();
      if (dialog) dialog.querySelectorAll('[data-cookie-purpose]').forEach((box) => { choices[box.dataset.cookiePurpose] = box.checked; });
      save(choices);
      return;
    }
    const opener = target.closest('[data-cookie-manage], [data-cookie-settings], a[href$="#cookie-settings"]');
    if (opener) {
      event.preventDefault();
      openSettings(opener);
    }
  });

  // If a choice is saved some other way, the banner goes.
  document.addEventListener('visitorConsentCollected', () => {
    hideBanner();
    loadGoogleBadge();
  });

  // Theme editor: the banner shows while its section is selected, so it can be styled.
  document.addEventListener('shopify:section:select', (event) => { if (event.target.querySelector('[data-cookie-banner]')) showBanner(); });
  document.addEventListener('shopify:section:load', (event) => { if (designMode() && event.target.querySelector('[data-cookie-banner]')) showBanner(); });
  document.addEventListener('shopify:section:deselect', (event) => { if (event.target.querySelector('[data-cookie-banner]')) hideBanner(); });

  function init() {
    if (window.location.hash === '#cookie-settings') openSettings(null);
    if (!rootEl() || designMode()) return;
    loadApi().then((api) => {
      if (!api) return;
      if (api.shouldShowBanner()) showBanner();
      loadGoogleBadge();
    });
  }

  // With no cookie settings to open (theme banner off and Shopify's banner off), hide the footer button.
  window.addEventListener('load', () => {
    if (dialogEl() || (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function')) return;
    document.querySelectorAll('[data-cookie-settings]').forEach((button) => {
      const item = button.closest('li');
      if (item) item.hidden = true;
      else button.hidden = true;
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
